import { type FC, useDeferredValue, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { dashboardDataTable as dt } from "src/components/common";
import type {
  SalesAttributionChannelComparisonRow,
  SalesAttributionChannelRow,
} from "../utils/salesAttributionTypes";
import ChannelRevenueBars from "./ChannelRevenueBars";
import ChannelScorecardTable from "./ChannelScorecardTable";
import { panelTooltip, SalesAttributionPanel } from "./salesAttributionShared";

interface SalesAttributionChannelsSectionProps {
  channelComparison: SalesAttributionChannelComparisonRow[];
  channelRows: SalesAttributionChannelRow[];
  scorecardTotal: SalesAttributionChannelRow | null;
}

export const SalesAttributionChannelsSection: FC<
  SalesAttributionChannelsSectionProps
> = ({ channelComparison, channelRows, scorecardTotal }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);

  const filteredRows = useMemo(() => {
    const term = deferredSearch.trim().toLowerCase();
    if (!term) {
      return channelRows;
    }
    return channelRows.filter(row => row.channelName.toLowerCase().includes(term));
  }, [channelRows, deferredSearch]);

  return (
    <>
      <SalesAttributionPanel
        title={t("salesAttribution.ui.panelTitles.channelRevenue", {
          defaultValue: "Zid vs GA4 by channel",
        })}
        definition={panelTooltip(
          t,
          "channelRevenue",
          "Green is Zid's store-actual revenue for the channel; amber is GA4's Analytics revenue for the same channel and window. The gap is measurement loss, not extra sales. Only channels both sources report revenue for appear."
        )}
      >
        <ChannelRevenueBars rows={channelComparison} />
      </SalesAttributionPanel>

      <SalesAttributionPanel
        title={t("salesAttribution.ui.panelTitles.scorecard", { defaultValue: "Channel scorecard" })}
        definition={panelTooltip(
          t,
          "channelScorecard",
          "Channel-wise breakdown of marketing performance across key metrics. Only Google Ads and Meta Ads are covered — the remaining channels need sources that are not integrated yet."
        )}
        actions={
          <div className="sales-attribution__panel-controls">
            <label className={`${dt.search} ${dt.searchSmall}`}>
              <Search size={16} aria-hidden />
              <input
                type="search"
                value={search}
                onChange={event => setSearch(event.target.value)}
                placeholder={t("salesAttribution.ui.searchChannelsPlaceholder", {
                  defaultValue: "Search channels...",
                })}
                aria-label={t("salesAttribution.ui.searchChannelsAria", {
                  defaultValue: "Search channels",
                })}
              />
            </label>
          </div>
        }
      >
        <ChannelScorecardTable rows={filteredRows} total={scorecardTotal} />
      </SalesAttributionPanel>
    </>
  );
};

export default SalesAttributionChannelsSection;
